import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';
import RainOverlay from '@/components/hydlib/RainOverlay';
import { useAuth } from '@/context/AuthContext';

const FEES_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Logged-in members with late fees see this strip at the top of the site */
function FeesNoticeStrip() {
    const { user, getAuthHeader } = useAuth();
    const [fees, setFees] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!user || user.role === 'staff') { setFees(null); return; }
        axios.get(`${FEES_API}/late-fees/my-fees`, { headers: getAuthHeader() })
            .then((res) => { if (!cancelled) setFees(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);
    const outstanding = Number(user?.outstanding_fees || fees?.total_outstanding || 0);
    const accruing = Number(fees?.total_accruing || 0);
    if (outstanding + accruing <= 0) return null;
    return (
        <div data-testid="fees-notice-strip" style={{ background: 'var(--a4)', color: 'var(--o4)', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap', fontFamily: "'Space Mono', monospace", fontSize: 12.5, letterSpacing: 0.5 }}>
            <span data-testid="fees-notice-text">
                {outstanding > 0 && <>₹{outstanding.toFixed(0)} in late fees due{accruing > 0 ? ' · ' : ''}</>}
                {accruing > 0 && <>₹{accruing.toFixed(0)} growing on overdue books still out (₹50/day)</>}
            </span>
            <Link to="/my-library?section=payments" data-testid="fees-notice-link" style={{ color: 'var(--o4)', textDecoration: 'underline', fontWeight: 700 }}>
                {outstanding > 0 ? 'Pay now →' : 'Return books to stop the meter →'}
            </Link>
        </div>
    );
}

/* Small helpers -------------------------------------------------------- */
const HL_A2 = {
    background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`,
};
const HL_A1 = {
    background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a1) 40%, var(--paper)) 5%, color-mix(in oklch, var(--a1) 26%, var(--paper)) 52%, color-mix(in oklch, var(--a1) 45%, var(--paper)) 94%, transparent 98.5%)`,
};
const HL_A3 = {
    background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a3) 38%, var(--paper)) 5%, color-mix(in oklch, var(--a3) 24%, var(--paper)) 52%, color-mix(in oklch, var(--a3) 42%, var(--paper)) 94%, transparent 98.5%)`,
};
const HL_A5 = {
    background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a5) 48%, var(--paper)) 5%, color-mix(in oklch, var(--a5) 32%, var(--paper)) 52%, color-mix(in oklch, var(--a5) 54%, var(--paper)) 94%, transparent 98.5%)`,
};

const scallop = (accent) => ({
    backgroundImage: `radial-gradient(circle at 50% 0, var(${accent}) 0 5px, transparent 6px)`,
});

/* Rain easter egg ------------------------------------------------------ */
function useRainDetector() {
    const [raining, setRaining] = useState(false);
    useEffect(() => {
        if (new URLSearchParams(window.location.search).get('rain') === '1') {
            setRaining(true);
            return undefined;
        }
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch(
                    'https://api.open-meteo.com/v1/forecast?latitude=17.41&longitude=78.44&current=rain,precipitation,weather_code'
                );
                const data = await res.json();
                const cur = data?.current || {};
                const code = Number(cur.weather_code || 0);
                const isRain =
                    (cur.rain > 0) ||
                    (cur.precipitation > 0.1) ||
                    (code >= 51 && code <= 67) ||
                    (code >= 80 && code <= 82) ||
                    (code >= 95 && code <= 99);
                if (!cancelled) setRaining(!!isRain);
            } catch {
                /* silent no-op */
            }
        };
        check();
        const id = setInterval(check, 30 * 60 * 1000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);
    return raining;
}

/* ---------------------------------------------------------------------- */

export default function LandingPage() {
    const navigate = useNavigate();
    const raining = useRainDetector();
    const heroRef = useRef(null);

    const goJoin = () => navigate('/signup');

    return (
        <PublicLayout theme="archive" banner="sunday">
            <FeesNoticeStrip />
            {/* Rain overlay */}
            <RainOverlay active={raining} />
            {raining && (
                <div className="hydlib-rain-badge" style={{
                    fontFamily: "'Caveat', cursive", fontSize: 24, color: 'var(--a1)',
                    background: 'var(--paper)', border: '1px solid var(--line)',
                    padding: '4px 12px 8px', textAlign: 'center',
                }} data-testid="rain-badge">
                    ☔ raining in Banjara Hills
                    <Link to="/catalog" data-testid="rain-badge-link" style={{ display: 'block', fontSize: 18, lineHeight: 1.2, color: 'var(--a3)' }}>perfect reading weather —<br />reserve your stack →</Link>
                </div>
            )}

            {/* ============= HERO ============= */}
            <section ref={heroRef} id="hero" className="hydlib-cols hydlib-pad" style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1.15fr 1fr',
                gap: 48,
                alignItems: 'center',
                padding: '72px 48px 88px',
                maxWidth: 1240,
                margin: '0 auto',
            }}>
                <div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 20 }}>
                        A subscription library for the incurably curious
                    </div>
                    <h1 className="hydlib-h1" style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 64, lineHeight: 1.06, margin: '0 0 24px' }}>
                        Unlimited books.<br />Five at a time.<br /><em style={{ color: 'var(--a1)' }}>Zero fuss.</em>
                    </h1>
                    <p style={{ fontSize: 18, lineHeight: 1.6, maxWidth: '46ch', margin: '0 0 32px' }}>
                        Browse online, reserve your stack, and collect it from our pickup box — six days a week. On Sundays, the doors swing open and the library is all yours.
                    </p>
                    <div style={{ position: 'relative', display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={goJoin}
                            data-testid="hero-join-cta"
                            className="hydlib-highlight"
                            style={{
                                cursor: 'pointer',
                                border: 'none',
                                color: 'var(--ink)',
                                fontFamily: "'Space Mono', monospace",
                                fontWeight: 700,
                                fontSize: 16,
                                letterSpacing: 1.5,
                                textTransform: 'uppercase',
                                padding: '10px 22px',
                                transform: 'rotate(-0.8deg)',
                                ...HL_A2,
                            }}
                        >
                            Join — ₹500/month
                        </button>
                        <span className="hydlib-hide-mobile" style={{ position: 'absolute', top: -32, left: 228, fontFamily: "'Caveat', cursive", fontSize: 21, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(6deg)', whiteSpace: 'nowrap' }}>
                            let the fun begin!
                        </span>
                        <a href="#how" style={{ fontFamily: "'Space Mono', monospace", fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', textDecoration: 'underline wavy 2px var(--a1)', textUnderlineOffset: 6 }}>
                            How it works ↓
                        </a>
                    </div>
                </div>

                {/* Est. 2026 HYD stamp — overlays hero top-right */}
                <div className="hydlib-hide-mobile" style={{
                    position: 'absolute', top: 34, right: 26, width: 108, height: 108,
                    border: '2px dashed var(--a4)', borderRadius: '50%', display: 'flex',
                    flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                    transform: 'rotate(12deg)', color: 'var(--a4)',
                    fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5,
                    textTransform: 'uppercase', textAlign: 'center', pointerEvents: 'none', zIndex: 3,
                }} data-testid="hero-stamp">
                    <span>Est.</span>
                    <span style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 26, letterSpacing: 0 }}>2026</span>
                    <span>HYD</span>
                </div>

                {/* Right column — stacked pill "book spines" */}
                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                    <PillLabel width={250} height={34} bg="var(--a3)" color="var(--paper)" rotate={-1.5} to="/catalog?category=Fiction">FICTION</PillLabel>
                    <PillLabel width={290} height={40} bg="var(--a4)" color="var(--paper)" rotate={1} to="/catalog?category=Poetry">POETRY!</PillLabel>
                    <PillLabel width={230} height={30} bg="var(--a2)" color="#231F1C" rotate={-2} to="/catalog?category=Graphic novel">COMICS</PillLabel>
                    <PillLabel width={300} height={44} bg="var(--a1)" color="var(--paper)" rotate={0.6} italic to="/catalog?category=History">history &amp; mystery</PillLabel>
                    <PillLabel width={260} height={34} bg="var(--a5)" color="var(--paper)" rotate={-1} to="/catalog?category=Children">KIDS&apos; TALES</PillLabel>
                    <div style={{ width: 320, borderBottom: '2px solid var(--line)', marginTop: 4 }} />
                    <div style={{ fontFamily: "'Libre Caslon Text', serif", fontStyle: 'italic', fontSize: 14, letterSpacing: 0.5, marginTop: 14, opacity: 0.75 }}>
                        Your next five, waiting in the box
                    </div>
                </div>
            </section>

            {/* Rolling ticker of recently added books */}
            <BookTicker />

            {/* Scallop */}
            <div className="hydlib-scallop" style={scallop('--a1')} />

            {/* ============= MEMBERSHIP PLANS ============= */}
            <section id="plans" style={{ position: 'relative', background: 'color-mix(in oklch, var(--a1) 10%, var(--paper))', padding: '80px 48px' }} className="hydlib-pad">
                <div className="hydlib-plans-grid" aria-hidden="true" />
                <div style={{ position: 'relative', maxWidth: 1240, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 44, margin: '0 0 8px', textAlign: 'center' }}>
                        <span style={{ background: 'var(--paper)', boxShadow: '8px 0 0 var(--paper), -8px 0 0 var(--paper)' }}>Membership</span>
                    </h2>
                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 48px', textAlign: 'center' }}>
                        <span style={{ background: 'var(--paper)', boxShadow: '6px 0 0 var(--paper), -6px 0 0 var(--paper)' }}>One membership. Two ways to pay.</span>
                    </p>
                    <div id="join" className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, maxWidth: 1080, margin: '0 auto' }}>
                        <Link to="/signup" data-testid="plan-monthly-card" style={{ display: 'block', color: 'inherit', textDecoration: 'none', border: '1px solid var(--line)', background: 'var(--bg)', padding: '32px 28px' }}>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Monthly</div>
                            <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 44 }}>₹500</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, margin: '6px 0 20px', opacity: 0.7 }}>per month</div>
                            <div style={{ fontSize: 15, lineHeight: 1.6 }}>Unlimited books, five at a time. Cancel whenever.</div>
                        </Link>
                        <Link to="/signup" data-testid="plan-yearly-card" style={{ display: 'block', textDecoration: 'none', border: '1px solid var(--line)', background: 'var(--a1)', color: 'var(--o1)', padding: '32px 28px', position: 'relative' }}>
                            <div style={{ position: 'absolute', top: -12, right: 14, background: 'var(--a2)', color: 'var(--o2)', fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, padding: '4px 10px', border: '1px solid var(--line)' }}>2 MONTHS FREE</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Yearly</div>
                            <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 44 }}>₹5,000</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, margin: '6px 0 20px', opacity: 0.8 }}>per year</div>
                            <div style={{ fontSize: 15, lineHeight: 1.6 }}>Same deal, twelve months, better math.</div>
                        </Link>
                        <Link to="/signup" data-testid="plan-kids-card" style={{ display: 'block', color: 'inherit', textDecoration: 'none', border: '1px solid var(--line)', background: 'var(--bg)', padding: '32px 28px', position: 'relative' }}>
                            <div style={{ position: 'absolute', top: -12, right: 14, background: 'var(--a3)', color: 'var(--o3)', fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1.5, padding: '4px 10px', border: '1px solid var(--line)' }}>FOR KIDS</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>Children&apos;s</div>
                            <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 44 }}>₹300</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, margin: '6px 0 20px', opacity: 0.7 }}>per month · ₹3,000/year</div>
                            <div style={{ fontSize: 15, lineHeight: 1.6 }}>Kids&apos; books only — on its own, or added to a grown-up&apos;s plan.</div>
                        </Link>
                        <div style={{ gridColumn: '1 / -1', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, flexWrap: 'wrap', padding: '8px 0 0' }}>
                            <span className="hydlib-hide-mobile" style={{ position: 'absolute', top: -22, left: 451, fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(-3deg)', whiteSpace: 'nowrap' }}>
                                read more, scroll less
                            </span>
                            <button
                                onClick={goJoin}
                                data-testid="plans-join-cta"
                                className="hydlib-highlight"
                                style={{
                                    cursor: 'pointer',
                                    border: 'none',
                                    color: 'var(--ink)',
                                    fontFamily: "'Libre Caslon Display', serif",
                                    fontSize: 28,
                                    padding: '6px 22px',
                                    transform: 'rotate(-0.6deg)',
                                    ...HL_A2,
                                }}
                            >
                                Become a member now →
                            </button>
                        </div>
                        <div style={{ gridColumn: '1 / -1', border: '1px dashed var(--line)', background: 'var(--bg)', padding: '18px 22px', marginTop: 26, textAlign: 'center', fontSize: 15.5, lineHeight: 1.6 }}>
                            <strong>Not ready to commit?</strong> Rent book by book —{' '}
                            <Link to="/catalog" className="hydlib-highlight" style={{ padding: '1px 8px', color: 'inherit', textDecoration: 'none', ...HL_A1 }}>₹200 per adult book</Link> ·{' '}
                            <Link to="/catalog?category=Children" className="hydlib-highlight" style={{ padding: '1px 8px', borderRadius: '0.6em 0.3em 0.8em 0.4em / 0.25em 0.7em 0.35em 0.6em', color: 'inherit', textDecoration: 'none', ...HL_A3 }}>₹100 per children&apos;s book</Link>.{' '}
                            Fill a cart in the <Link to="/catalog" style={{ color: 'var(--a3)' }}>catalog</Link>, pay once, done.
                        </div>
                    </div>
                </div>
            </section>

            <div className="hydlib-scallop" style={{ ...scallop('--a3'), backgroundColor: 'var(--paper)' }} />

            {/* ============= ABOUT ============= */}
            <section id="about" style={{ padding: '80px 48px' }} className="hydlib-pad">
                <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 44, margin: '0 0 20px' }}>A library that fits your week</h2>
                    <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'start', marginBottom: 48 }}>
                        <p style={{ fontSize: 17, lineHeight: 1.7, margin: 0 }}>
                            We started The Hyderabad Library with one belief: reading should be accessible and effortless, even when your budget or schedule isn&apos;t. So we moved the queue online, put the books in a box accessible outside regular working hours, and saved the browsing-for-hours magic for Sundays.
                        </p>
                        <div>
                            <p style={{ fontSize: 17, lineHeight: 1.7, margin: '0 0 24px' }}>No fines drama, no membership mazes — just a steady stream of good books, a great community and one open door a week.</p>
                            <div style={{ fontFamily: "'Libre Caslon Text', serif", fontSize: 15, lineHeight: 2 }}>
                                📍 2nd Floor, BBR Forum, Road No. 2 Banjara Hills (same building as KSR Eyecare) · pickup box in the parking space<br />
                                🕰 Online Mon–Sat · Open doors Sundays 10.30am–5pm<br />
                                ✉️ <a href="mailto:thehyderabadlibrary@gmail.com" style={{ color: 'var(--a3)' }}>thehyderabadlibrary@gmail.com</a><br />
                                📞 7901662333
                            </div>
                        </div>
                    </div>

                    <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                        <ModeCard tone="a3" tint="var(--o3)" kicker="Mon–Sat" title="Weekdays: the 2-minute library" chips={[{ label: 'Reserve online', to: '/catalog' }, 'Box', 'Go']} chipSep="→" rotate={-0.6}
                            note="for weeks that run on coffee" notePosition="top">
                            For weeks with no time to spare. Pick your stack from the catalog, wait for the email, and lift your books from the pickup box — no browsing, no queue, no small talk.
                        </ModeCard>
                        <ModeCard tone="a1" tint="var(--o1)" kicker="Sundays" title="Sundays: the slow library" chips={[{ label: 'Browse', to: '/catalog' }, 'Read', { label: 'Events', to: '/events' }]} chipSep="·" rotate={0.5}
                            note="our favourite day of the week" notePosition="bottom">
                            For the seventh day, when time stretches. Wander the stacks, settle into a corner, sign up in person, attend an event — or host your own.
                        </ModeCard>
                    </div>
                </div>
            </section>

            <div className="hydlib-scallop" style={scallop('--a2')} />

            {/* ============= HOW IT WORKS ============= */}
            <section id="how" style={{ background: 'var(--paper)', padding: '80px 48px' }} className="hydlib-pad">
                <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                    <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 44, margin: '0 0 8px' }}>How it works</h2>
                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 48px' }}>Five steps. Like your five books.</p>
                    <div className="hydlib-steps" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 24 }}>
                        <Step n="01" accent="a1" fg="var(--o1)" rot={-2} title="Subscribe" body="₹500 a month or ₹5,000 a year. Unlimited books — five at a time." to="/signup" />
                        <Step n="02" accent="a2" fg="var(--o2)" rot={1.5} title="Browse & reserve" body="Mon–Sat everything happens online. Pick your stack from the catalog." to="/catalog" />
                        <Step n="03" accent="a3" fg="var(--o3)" rot={-1} title="Get the email" body="Your books land in the pickup box. No small talk required." />
                        <Step n="04" accent="a4" fg="var(--o4)" rot={2} title="Read for 21 days" body="Extend for free, as long as nobody's holding your book hostage." />
                        <Step n="05" accent="a5" fg="var(--o5)" rot={-1.5} title="Return — or don't move" body="Drop them back, or let Thursday delivery swap old books for new." to="/faqs" />
                    </div>

                    <div className="hydlib-cols hydlib-fineprint-row" style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 18px', marginTop: 32, paddingLeft: 150, fontFamily: "'Libre Caslon Text', serif", fontSize: 16 }}>
                        <span className="hydlib-hide-mobile" style={{ position: 'absolute', left: 0, top: 2, fontFamily: "'Caveat', cursive", fontSize: 24, fontWeight: 600, color: 'var(--a4)', transform: 'rotate(-2deg)', whiteSpace: 'nowrap' }}>the fine print →</span>
                        <FinePrintChip style={HL_A2} rotate={-0.5} to="/faqs">📚 5 books at a time</FinePrintChip>
                        <FinePrintChip style={HL_A5} rotate={0.4} to="/faqs">🗓 21-day loans, free extensions if no holds</FinePrintChip>
                        <FinePrintChip style={HL_A1} rotate={-0.3} to="/faqs">🚲 Doorstep delivery every Thursday (paid)</FinePrintChip>
                        <Link to="/faqs" style={{ color: 'inherit', textDecoration: 'underline wavy 2px var(--a4)', textUnderlineOffset: 5 }}>⏰ Extensions are free when nobody&apos;s waiting. Past due with a hold: ₹50/day per book</Link>
                    </div>
                </div>
            </section>

            {/* Scallop divider between How and Sundays */}
            <div className="hydlib-scallop" style={{ ...scallop('--a4'), backgroundColor: 'var(--bg)' }} />

            {/* ============= SUNDAYS / THE SEVENTH DAY ============= */}
            <SundaysSection />

            {/* Scallop */}
            <div className="hydlib-scallop" style={{ ...scallop('--a2'), backgroundColor: 'var(--paper)' }} />

            {/* ============= THE SPACE ============= */}
            <SpaceSection />

        </PublicLayout>
    );
}

/* ---------------------------------------------------------------------- */
/* Sub-components                                                          */
/* ---------------------------------------------------------------------- */

function PillLabel({ width, height, bg, color, rotate = 0, italic = false, to = '/catalog', children }) {
    return (
        <Link to={to} className="hydlib-doodle hydlib-tilt-hover" style={{
            position: 'relative',
            width, height, background: bg, transform: `rotate(${rotate}deg)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color,
            fontFamily: italic ? "'Libre Caslon Display', serif" : "'Space Mono', monospace",
            fontSize: italic ? 18 : 12,
            letterSpacing: italic ? 0 : 2,
            fontStyle: italic ? 'italic' : 'normal',
            overflow: 'hidden',
            textDecoration: 'none',
        }}>{children}</Link>
    );
}

function ModeCard({ tone, tint, kicker, title, chips, chipSep = '→', rotate = 0, note, notePosition = 'top', children }) {
    const arrow = (
        <svg width="46" height="49" viewBox="0 0 34 34" style={{ ...(notePosition === 'bottom' ? { marginBottom: 12, transform: 'scaleY(-1)' } : { marginTop: 10 }) }} aria-hidden="true">
            <path d="M6 4 C 16 8, 24 16, 27 28" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" />
            <path d="M20 24 l7 5 2 -9" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
    return (
        <div style={{
            position: 'relative',
            border: '1px solid var(--line)',
            background: `var(--${tone})`,
            color: tint,
            padding: '32px 30px',
            transform: `rotate(${rotate}deg)`,
        }}>
            {note && (
                <div className="hydlib-hide-mobile" style={{
                    position: 'absolute',
                    ...(notePosition === 'bottom'
                        ? { bottom: -40, right: 28, display: 'flex', alignItems: 'flex-end', gap: 6 }
                        : { top: -44, left: 24, display: 'flex', alignItems: 'flex-start', gap: 6 }),
                    color: `var(--${tone})`,
                    pointerEvents: 'none',
                }}>
                    {notePosition === 'bottom' && arrow}
                    <span style={{
                        fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600,
                        transform: `rotate(${notePosition === 'bottom' ? -3 : -4}deg)`,
                        whiteSpace: 'nowrap',
                    }}>{note}</span>
                    {notePosition === 'top' && arrow}
                </div>
            )}
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>{kicker}</div>
            <h3 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 28, margin: '0 0 16px' }}>{title}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', flexWrap: 'wrap', marginBottom: 16 }}>
                {chips.map((raw, i) => {
                    const c = typeof raw === 'string' ? { label: raw } : raw;
                    const chipStyle = { border: '1px solid var(--line)', padding: '8px 14px', background: 'var(--paper)', color: 'var(--ink)', textDecoration: 'none' };
                    return (
                        <span key={c.label}>
                            {c.to
                                ? <Link to={c.to} style={chipStyle}>{c.label}</Link>
                                : <span style={chipStyle}>{c.label}</span>}
                            {i < chips.length - 1 ? <span style={{ margin: '0 6px' }}>{chipSep}</span> : null}
                        </span>
                    );
                })}
            </div>
            <p style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>{children}</p>
        </div>
    );
}

function Step({ n, accent, fg, rot, title, body, to }) {
    const Tag = to ? Link : 'div';
    return (
        <Tag {...(to ? { to } : {})} style={{ border: '1px solid var(--line)', padding: '24px 20px', background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 12, color: 'inherit', textDecoration: 'none' }}>
            <div className="hydlib-tilt-hover" style={{
                alignSelf: 'flex-start',
                background: `var(--${accent})`, color: fg,
                border: '1px solid var(--line)', padding: '6px 14px',
                fontFamily: "'Libre Caslon Display', serif", fontSize: 34, lineHeight: 1,
                transform: `rotate(${rot}deg)`,
            }}>{n}</div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1, textTransform: 'uppercase' }}>{title}</div>
            <div style={{ fontSize: 15, lineHeight: 1.55 }}>{body}</div>
        </Tag>
    );
}

function FinePrintChip({ style, rotate, to = '/faqs', children }) {
    return (
        <Link to={to} className="hydlib-highlight hydlib-fineprint-chip" style={{
            display: 'inline-block', whiteSpace: 'nowrap',
            padding: '4px 12px', transform: `rotate(${rotate}deg)`,
            color: 'inherit', textDecoration: 'none',
            ...style,
        }}>{children}</Link>
    );
}

/* ---------------- Book ticker ---------------- */
function BookTicker() {
    const [books, setBooks] = useState([]);
    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books?sort_by=recent&limit=30`)
            .then(r => r.json())
            .then(d => setBooks((d?.books || []).filter(b => b.cover_image_url).slice(0, 18)))
            .catch(() => { /* ignore */ });
    }, []);
    if (books.length === 0) return null;
    const run = (hidden) => (
        <span style={{ display: 'inline-flex', alignItems: 'center' }} aria-hidden={hidden || undefined}>
            {books.map(b => (
                <Link key={`${hidden ? 'b' : 'a'}-${b.id}`} to={`/book/${b.id}`} title={b.title} style={{ display: 'inline-flex', margin: '0 14px' }}>
                    <img src={b.cover_image_url} alt={b.title} loading="lazy" style={{ height: 118, width: 82, objectFit: 'cover', border: '1px solid var(--line)', background: 'var(--paper)', boxShadow: '2px 3px 0 color-mix(in oklch, var(--line) 35%, transparent)' }} />
                </Link>
            ))}
        </span>
    );
    return (
        <div data-testid="book-ticker" style={{ overflow: 'hidden', background: 'var(--a1)', padding: '12px 0 10px' }}>
            <div className="hydlib-marquee-track" style={{ alignItems: 'center' }}>
                {run(false)}
                {run(true)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '4px 18px', marginTop: 10, padding: '0 20px', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--o1)' }} data-testid="ticker-see-all">
                <span style={{ opacity: 0.75 }}>Fresh on the shelf — see all:</span>
                <Link to="/catalog?filter=new-adults" data-testid="ticker-new-adults-link" style={{ color: 'var(--o1)', textDecoration: 'underline', textUnderlineOffset: 3 }}>for the grown-ups →</Link>
                <Link to="/catalog?filter=new-kids" data-testid="ticker-new-kids-link" style={{ color: 'var(--o1)', textDecoration: 'underline', textUnderlineOffset: 3 }}>for the little ones →</Link>
            </div>
        </div>
    );
}

/* ---------------- Sundays (The Seventh Day) ---------------- */
function SundaysSection() {
    // Auto-generated calendar with Sundays (open days) highlighted; arrows flip months
    const [monthOffset, setMonthOffset] = useState(0);
    const [closedSundays, setClosedSundays] = useState(new Set());
    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sunday-closures`)
            .then(r => r.json())
            .then(d => setClosedSundays(new Set(Array.isArray(d) ? d : [])))
            .catch(() => { /* ignore */ });
    }, []);
    const now = new Date();
    const view = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = view.getFullYear();
    const month0 = view.getMonth();
    const firstDow = new Date(year, month0, 1).getDay();
    const daysInMonth = new Date(year, month0 + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push({ label: '', highlight: false });
    for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        cells.push({ label: String(d), highlight: new Date(year, month0, d).getDay() === 0 && !closedSundays.has(iso) });
    }
    const monthName = view.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const [events, setEvents] = useState([]);
    useEffect(() => {
        const accents = ['--a2', '--a3', '--a1', '--a4'];
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/addons/upcoming`)
            .then(r => r.json())
            .then(d => {
                const upcoming = Array.isArray(d?.upcoming) ? d.upcoming : [];
                setEvents(upcoming.slice(0, 4).map((a, i) => {
                    const accent = accents[i % accents.length];
                    let price = 'Members only';
                    if (a.external_registration_url) {
                        const p = Number(a.base_price ?? 0);
                        price = p === 0 ? 'Free · partner-hosted' : `₹${p} · partner-hosted`;
                    } else if (Array.isArray(a.ticket_categories_view) && a.ticket_categories_view.length) {
                        const avail = a.ticket_categories_view.filter(c => !c.sold_out && !c.expired);
                        const prices = avail.map(c => c.your_price).filter(p => p != null).map(Number);
                        if (!avail.length) price = 'Sold out';
                        else if (!prices.length) price = 'Members only';
                        else price = Math.min(...prices) === 0 ? 'Free · pick a ticket' : `from ₹${Math.min(...prices)}`;
                    } else if (a.your_audience !== 'blocked') {
                        const p = Number(a.your_price ?? a.base_price ?? 0);
                        const walkIns = a.attendance_policy === 'walk_ins';
                        if (p === 0) price = walkIns ? 'Free · walk-ins welcome' : 'Free · reservations required';
                        else price = walkIns ? `₹${p} · walk-ins welcome` : `₹${p}`;
                    }
                    let time = '';
                    try {
                        const f = (iso) => new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' }).toLowerCase();
                        if (a.event_datetime) time = f(a.event_datetime) + (a.event_end_datetime ? ` – ${f(a.event_end_datetime)}` : '');
                    } catch { /* keep */ }
                    let date = 'Coming soon';
                    try { date = new Date(a.event_datetime).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' }).replace(/,/g, ' ·'); } catch { /* keep */ }
                    return { date, time, title: a.name || 'Untitled', desc: (a.description || '').slice(0, 80), price, bg: `var(${accent})`, fg: `var(--o${accent.slice(-1)})`, tilt: (i % 2 === 0 ? -1 : 1) * (0.8 + (i % 3) * 0.3) };
                }));
            })
            .catch(() => { /* keep empty */ });
    }, []);
    const blankNotes = [
        { bg: 'var(--a2)', fg: 'var(--o2)', tilt: -1.5, note: 'nothing scheduled yet…' },
        { bg: 'var(--a3)', fg: 'var(--o3)', tilt: 1, note: '' },
        { bg: 'var(--a1)', fg: 'var(--o1)', tilt: -0.8, note: 'watch this space' },
    ];
    const dayNames = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
    return (
        <section id="sundays" style={{ background: 'var(--paper)', padding: '80px 48px' }} className="hydlib-pad">
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 44, margin: '0 0 8px' }}>The Seventh Day</h2>
                <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 56, alignItems: 'start', marginBottom: 56 }}>
                    <div>
                        <p style={{ fontSize: 18, lineHeight: 1.6, maxWidth: '55ch', margin: '0 0 20px' }}>
                            Monday to Saturday, we&apos;re a library you never see. But every <strong>Sunday</strong>, the doors open: browse the stacks, read in a corner, sign up in person, attend an event — or host one.
                        </p>
                        <p style={{ fontFamily: "'Libre Caslon Text', serif", fontSize: 15, lineHeight: 1.7, opacity: 0.85, margin: 0 }}>Highlighted dates are open days. Tap one — or head to the <Link to="/events" style={{ color: 'var(--a3)' }}>events page</Link> — to see what&apos;s on.</p>
                    </div>
                    <div style={{ border: '1px solid var(--line)', background: 'var(--paper)', padding: '20px 22px 24px', boxShadow: '6px 6px 0 color-mix(in oklch, var(--line) 30%, transparent)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: "'Libre Caslon Display', serif", fontSize: 20, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
                            <button onClick={() => setMonthOffset(o => o - 1)} data-testid="sundays-cal-prev" aria-label="Previous month" style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--ink)', opacity: 0.6, fontSize: 22, fontFamily: 'inherit', padding: '0 8px' }}>‹</button>
                            <span data-testid="sundays-calendar-month">{monthName}</span>
                            <button onClick={() => setMonthOffset(o => o + 1)} data-testid="sundays-cal-next" aria-label="Next month" style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--ink)', opacity: 0.6, fontSize: 22, fontFamily: 'inherit', padding: '0 8px' }}>›</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, fontFamily: "'Libre Caslon Text', serif", fontSize: 14, textAlign: 'center' }}>
                            {dayNames.map(dn => (
                                <div key={dn} style={{ padding: '6px 0', fontStyle: 'italic', letterSpacing: 1, borderBottom: '1px solid var(--line)', color: 'var(--a1)' }}>{dn}</div>
                            ))}
                            {cells.map((c, i) => (
                                c.highlight
                                    ? <Link key={i} to="/events" style={{ display: 'block', padding: '9px 0', background: 'var(--a2)', color: 'var(--o2)', fontWeight: 700, border: '1px solid var(--line)', textDecoration: 'none' }}>{c.label}</Link>
                                    : <div key={i} style={{ padding: '9px 0', background: 'transparent', color: 'inherit', fontWeight: 400, border: '1px solid transparent' }}>{c.label}</div>
                            ))}
                        </div>
                        <div style={{ fontFamily: "'Libre Caslon Text', serif", fontStyle: 'italic', fontSize: 13, letterSpacing: 0.5, marginTop: 12, opacity: 0.75 }}>■ Sundays = open doors, 10.30am–5pm</div>
                    </div>
                </div>

                <h3 style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 24px' }} data-testid="upcoming-events-title">Upcoming Events <Link to="/events" data-testid="see-all-events-link" style={{ color: 'var(--a3)', marginLeft: 12, fontSize: 12 }}>see all →</Link></h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28 }}>
                    {events.length === 0 ? blankNotes.map((n, i) => (
                        <Link to="/events" key={i} className="hydlib-tilt-hover" style={{ position: 'relative', background: n.bg, color: n.fg, width: 220, minHeight: 140, padding: '26px 24px 22px', boxSizing: 'border-box', boxShadow: '3px 10px 16px color-mix(in oklch, var(--line) 40%, transparent)', transform: `rotate(${n.tilt}deg)`, textDecoration: 'none' }} data-testid={`sunday-blank-${i}`}>
                            {n.note && <div style={{ fontFamily: "'Caveat', cursive", fontSize: 21, fontWeight: 600, opacity: 0.75, transform: 'rotate(-1deg)', marginTop: 44 }}>{n.note}</div>}
                        </Link>
                    )) : events.map((ev, i) => (
                        <Link to="/events" key={i} className="hydlib-tilt-hover" style={{ position: 'relative', display: 'block', background: ev.bg, color: ev.fg, padding: '26px 24px 22px', width: 220, minHeight: 140, boxShadow: '3px 10px 16px color-mix(in oklch, var(--line) 40%, transparent)', transform: `rotate(${ev.tilt}deg)`, textDecoration: 'none', boxSizing: 'border-box' }} data-testid={`sunday-event-${i}`}>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: ev.time ? 2 : 10, opacity: 0.85 }}>{ev.date}</div>
                            {ev.time && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 1, marginBottom: 10, opacity: 0.8 }}>{ev.time}</div>}
                            <div style={{ fontFamily: "'Libre Caslon Display', serif", fontSize: 22, lineHeight: 1.25 }}>{ev.title}</div>
                            <div style={{ fontFamily: "'Libre Caslon Text', serif", fontSize: 14, marginTop: 12, opacity: 0.9 }}>{ev.desc}</div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1, marginTop: 8 }}>{ev.price}</div>
                        </Link>
                    ))}
                </div>
                <p style={{ fontFamily: "'Libre Caslon Text', serif", fontSize: 15, marginTop: 36 }}>
                    Got an idea for an event? <a href="mailto:thehyderabadlibrary@gmail.com?subject=Sunday%20event%20pitch" style={{ color: 'var(--a3)' }}>Pitch it — Sundays are community-run.</a>
                </p>
            </div>
        </section>
    );
}

/* ---------------- The Space ---------------- */
function SpaceSection() {
    return (
        <section id="space" style={{ padding: '80px 48px' }} className="hydlib-pad">
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 44, margin: '0 0 40px', textAlign: 'center' }}>The space</h2>
                <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 28 }}>
                    <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a3)', background: 'var(--paper)', padding: '28px 26px' }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a3)', marginBottom: 14 }}>Where</div>
                        <div style={{ fontSize: 16, lineHeight: 1.65 }}>2nd Floor, BBR Forum,<br />Road No. 2 Banjara Hills, Hyderabad<br /><span style={{ fontSize: 14, opacity: 0.8 }}>Landmark: same building as KSR Eyecare</span></div>
                    </div>
                    <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', background: 'var(--paper)', padding: '28px 26px' }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a2)', marginBottom: 14 }}>When</div>
                        <div style={{ fontSize: 16, lineHeight: 1.65 }}>Mon–Sat: online only — pickup box open all day.<br />Sundays: doors open 10.30am–5pm.</div>
                    </div>
                    <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', background: 'var(--paper)', padding: '28px 26px' }}>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 14 }}>Can I sit and read?</div>
                        <div style={{ fontSize: 16, lineHeight: 1.65 }}>On Sundays, absolutely — stay as long as you like. Weekdays are lending-only.</div>
                    </div>
                </div>
                <iframe
                    title="Library location on map"
                    src="https://www.google.com/maps?q=The+Hyderabad+Library,+BBR+Forum,+Park+View+Enclave,+Banjara+Hills,+Hyderabad&output=embed"
                    style={{ width: '100%', height: 260, border: '1px solid var(--line)' }}
                    data-testid="landing-map"
                />
                <a href="https://maps.app.goo.gl/2EucnVthv76SzcCw6" target="_blank" rel="noopener noreferrer" data-testid="landing-map-link" style={{ display: 'inline-block', marginTop: 10, fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--a3)' }}>Open in Google Maps →</a>
            </div>
        </section>
    );
}

